import React from 'react';
import { Card, Tag, Tooltip, Alert, Space, Typography } from 'antd';
import {
  NumberOutlined, FieldStringOutlined, CalendarOutlined,
  CheckSquareOutlined, WarningOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/* ── Helpers ──────────────────────────────────────────────────────── */
function fmtNum(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return String(v);
  if (Number.isInteger(v)) return v.toLocaleString();
  return Number(v.toFixed(4)).toLocaleString();
}

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function typeIcon(t) {
  switch (t) {
    case 'numeric': return <NumberOutlined />;
    case 'temporal': return <CalendarOutlined />;
    case 'boolean': return <CheckSquareOutlined />;
    case 'categorical':
    default: return <FieldStringOutlined />;
  }
}

/* ── Per-column card ──────────────────────────────────────────────── */
function ColumnCard({ name, stats }) {
  const t = stats?.type || 'categorical';
  return (
    <Card
      size="small"
      style={{ minWidth: 240, flex: '0 0 auto' }}
      bodyStyle={{ padding: 12 }}
      title={
        <Space size={6}>
          {typeIcon(t)}
          <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{name}</Text>
          <Tag style={{ marginLeft: 4 }}>{t}</Tag>
        </Space>
      }
    >
      {t === 'numeric' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12 }}>
          <span>min: <b>{fmtNum(stats.min)}</b></span>
          <span>max: <b>{fmtNum(stats.max)}</b></span>
          <span>avg: <b>{fmtNum(stats.avg)}</b></span>
          <span>p50: <b>{fmtNum(stats.p50)}</b></span>
          <span>p90: <b>{fmtNum(stats.p90)}</b></span>
          <span>sum: <b>{fmtNum(stats.sum)}</b></span>
          <span>distinct: <b>{fmtNum(stats.distinct_count)}</b></span>
          <span>nulls: <b>{fmtNum(stats.null_count)}</b></span>
        </div>
      )}
      {t === 'categorical' && (
        <>
          <div style={{ fontSize: 12, marginBottom: 6 }}>
            distinct: <b>{fmtNum(stats.distinct_count)}</b>{' · '}
            nulls: <b>{fmtNum(stats.null_count)}</b>
          </div>
          {Array.isArray(stats.top) && stats.top.length > 0 && (
            <div style={{ fontSize: 12 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Top values</Text>
              <div style={{ marginTop: 2 }}>
                {stats.top.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Tooltip title={String(t.value)}>
                      <span style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: 140,
                      }}>
                        {String(t.value)}
                      </span>
                    </Tooltip>
                    <span style={{ color: '#888' }}>{fmtNum(t.count)} · {fmtPct(t.pct)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {t === 'temporal' && (
        <div style={{ fontSize: 12, display: 'grid', gap: 2 }}>
          <span>min: <b>{stats.min ?? '—'}</b></span>
          <span>max: <b>{stats.max ?? '—'}</b></span>
          <span>range: <b>{fmtNum(stats.range_days)}</b> days</span>
          <span>distinct: <b>{fmtNum(stats.distinct_count)}</b></span>
          <span>nulls: <b>{fmtNum(stats.null_count)}</b></span>
        </div>
      )}
      {t === 'boolean' && (
        <div style={{ fontSize: 12 }}>
          true: <b>{fmtNum(stats.true_count)}</b> · false: <b>{fmtNum(stats.false_count)}</b>
          {' · '}nulls: <b>{fmtNum(stats.null_count)}</b>
        </div>
      )}
    </Card>
  );
}

/* ── Main panel ───────────────────────────────────────────────────── */
const QuickStatsPanel = ({ dataSummary, sanityWarnings }) => {
  if (!dataSummary && (!sanityWarnings || !sanityWarnings.length)) {
    return null;
  }
  const byCol = (dataSummary && dataSummary.by_column) || {};
  const cols = Object.keys(byCol);
  return (
    <div style={{ marginTop: 12 }}>
      {Array.isArray(sanityWarnings) && sanityWarnings.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {sanityWarnings.map((w, i) => (
            <Alert
              key={i}
              type="warning"
              icon={<WarningOutlined />}
              showIcon
              banner
              message={w.kind?.replace(/_/g, ' ')}
              description={w.message}
              style={{ marginBottom: 4 }}
            />
          ))}
        </div>
      )}
      {dataSummary && (
        <Card
          size="small"
          title={
            <Space size={6}>
              <Text strong>Quick stats</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                · {fmtNum(dataSummary.row_count)} rows
                · {fmtNum(dataSummary.column_count)} cols
                {dataSummary.null_cells_total > 0 && (
                  <> · {fmtNum(dataSummary.null_cells_total)} nulls</>
                )}
              </Text>
            </Space>
          }
          bodyStyle={{ padding: 8 }}
        >
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {cols.map((c) => (
              <ColumnCard key={c} name={c} stats={byCol[c]} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default QuickStatsPanel;
